import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { formInitialize, formSet, formUninitialize, useCpFormSubmit } from "../gvc/testNewForm/utils";
import { useAppDispatch } from "../../store/hooks";
import { formActions } from "../../store/formSlice";
import { Modal, notification } from "antd";
import { linksOf, request } from "../../services/cpln";
import { CpFormPromptDetail } from "../../components/antd/form/CpFormPromptDetail";
import { CpFormContext } from "../../components/form/CpFormContext";
import { CpFormValidator } from "../gvc/testNewForm/CpFormValidator";
import { CpPreviewModal } from "../workload/draft/previewModal";
import { CpDebugModal } from "../workload/draft/debugModal";
import { Mk8sCluster } from "../../schema/types/mk8s/entity/mk8s";
import { useGetMk8sClusterQuery } from "../../store/api/mk8sApi";
import { mk8sToInput } from "./draft/utils/mk8sToInput";
import { mk8sToPatch } from "./draft/utils/utils";
import { inputToMk8s } from "./draft/utils/inputToMk8s";
import { Mk8sInput } from "../../schema/types/mk8s/input/mk8s";
import { Mk8sDetailContent } from "./detailContent";
import { getValidator } from "./draft/validator";
import { getDirtyChecker } from "./draft/dirtyChecker";
import { CpFormDirtyChecker } from "../gvc/testNewForm/CpFormDirtyChecker";

interface Props {
  mk8s: Mk8sCluster;
}
export const Mk8sDetailForm: React.FC<Props> = ({ mk8s }) => {
  const dispatch = useAppDispatch();
  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  const { refetch } = useGetMk8sClusterQuery(linksOf(mk8s).self!);

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = mk8sToInput(mk8s);
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // confirm
  React.useEffect(() => {
    dispatch(formActions.setInitialValues({ id: formId, values: mk8sToInput(mk8s) }));
  }, [mk8s.version]);

  // validation/dirty/submit
  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const dirtyCheckerRef = React.useRef(getDirtyChecker(formId));
  const dirtyChecker = dirtyCheckerRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(input: Mk8sInput) {
    try {
      let body = mk8sToPatch(inputToMk8s(input), { id: input.id, version: input.version });
      const method = "patch";
      const url = linksOf(mk8s).self!;

      await request({
        method: method,
        url: url,
        body: body,
      });

      formSet(formId, `meta.applied`, true);

      notification.success({
        message: "Success",
        description: "Mk8s Cluster is updated",
      });

      refetch();
    } catch (e) {
      if (e?.response?.status === 409) {
        Modal.warning({
          title: "Update Conflict",
          content: "This resource has been modified since you opened it. The form will be refreshed with the latest data and your changes will be lost.",
          onOk: () => {
            refetch();
          },
        });
        return;
      }

      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <Mk8sDetailContent mk8s={mk8s} refetch={refetch} />
      <CpFormValidator validationFn={validator} />
      <CpFormDirtyChecker checker={dirtyChecker} />
      <CpFormPromptDetail link={linksOf(mk8s).self!} inputToItemFn={inputToMk8s} />
      <CpPreviewModal inputToItemFn={inputToMk8s} kind="mk8s" namePath="name" title="Mk8s Cluster" />
      <CpDebugModal inputToItemFn={inputToMk8s} kind="mk8s" namePath="name" title="Mk8s Cluster" />
    </CpFormContext.Provider>
  );
};
